import * as Sentry from '@sentry/react';
import axios from 'axios';
import { z } from 'zod';

// Constants
const ROUTE = '/authorize'; // POST

// Types
export const UserTypeStruct = z.enum([
  'BORROWER',
  'CAPITAL_PROVIDER',
  'SUPER_USER',
]);
export type UserType = z.infer<typeof UserTypeStruct>;
const AccessTokenStruct = z.object({
  raw: z.string(),
  payload: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    userType: UserTypeStruct,
    iat: z.number().int().positive().optional(), // Issued at
    exp: z.number().int().positive().optional(), // Expires at
  }),
});
export type AccessToken = z.infer<typeof AccessTokenStruct>;
const OrganizationStruct = z.object({
  id: z.string().uuid(),
  industry: z.string().optional().nullable(),
  name: z.string(),
  slug: z.string(),
});
export const UserStruct = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  userType: UserTypeStruct,
  isActive: z.boolean(),
  roleType: z.enum(['ADMIN', 'MEMBER', 'CONTRIBUTOR']),
  firstName: z.string(),
  lastName: z.string(),
  organizationId: z.string().uuid(),
  organization: OrganizationStruct,
});
export type User = z.infer<typeof UserStruct>;
const ResponseLoggedInStruct = z.object({
  status: z.literal('logged-in'),
  accessToken: AccessTokenStruct,
  user: UserStruct,
});
export type ResponseLoggedIn = z.infer<typeof ResponseLoggedInStruct>;
const ResponseLoggedOutStruct = z.object({
  status: z.literal('logged-out'),
  accessToken: z.null(),
  user: z.null(),
});
export type ResponseLoggedOut = z.infer<typeof ResponseLoggedOutStruct>;

// Exports
export const authorizeRequest = async () => {
  try {
    const response = await axios.post(ROUTE, undefined, {
      withCredentials: true,
    });

    // Parse logged-in response
    return ResponseLoggedInStruct.parse(response.data);

    // Parse logged-out response
  } catch (error) {
    // Expected 401 errors
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        return ResponseLoggedOutStruct.parse(error.response.data);
      }
      if (error.code === 'ECONNABORTED') {
        // Request aborted, almost certainly because of redirect. This can occur if a user navigates
        // to the `/auth/logout` route on admin, but isn't logged in.
        // !TODO @eldarshamukhamedov Probably need to get rid of the `/auth/logout` admin route
        Sentry.captureMessage(error.message, Sentry.Severity.Warning);
        throw error;
      }
    }
    // All other errors
    Sentry.captureException(error);
    throw error;
  }
};
